import { socketClient } from '../network/socket.js';
import type {
  CompleteSessionResponse,
  LobbyState,
  PlayConfig,
} from '@multirx/shared/types/championship.js';
import type { BoardStatePayload } from '@multirx/shared/types/board.js';

export const MEETING_SOCKET_EVENTS = {
  JOIN_LOBBY: 'meeting:join_lobby',
  CLAIM_DRIVER: 'meeting:claim_driver',
  RELEASE_DRIVER: 'meeting:release_driver',
  SET_READY: 'meeting:set_ready',
  START: 'meeting:start',
  RECLAIM_RACE: 'meeting:reclaim_race',
  LOBBY_STATE: 'meeting:lobby_state',
  PLAY_CONFIG: 'meeting:play_config',
  RACE_COMPLETE: 'meeting:race_complete',
  ERROR: 'meeting:error',
} as const;

/** Miroir client de `BOARD_EVENTS` (server/src/socket/boardHandlers.ts) — même
 * convention que `MEETING_SOCKET_EVENTS` : dupliqué plutôt que centralisé. */
export const BOARD_EVENTS = {
  WARMUP_START: 'board:warmup_start',
  STATE: 'board:state',
  ROLL: 'board:roll',
  DECISION: 'board:decision',
  CASINO_BET: 'board:casino_bet',
  WARMUP_END: 'board:warmup_end',
  ERROR: 'board:error',
  ADMIN_TAKE_CONTROL: 'board:admin_take_control',
  RECLAIM: 'board:reclaim',
} as const;

export function connectMeetingLobby(
  sessionId: number,
  claimerId: string,
  handlers: {
    onLobby: (lobby: LobbyState) => void;
    onPlayConfig?: (payload: { claimerId: string; config: PlayConfig }) => void;
    onWarmupStart?: (state: BoardStatePayload) => void;
    onError?: (message: string) => void;
  }
): () => void {
  if (!socketClient.connected) socketClient.connect();
  socketClient.emit(MEETING_SOCKET_EVENTS.JOIN_LOBBY, { sessionId, claimerId });

  const onLobby = (lobby: LobbyState) => handlers.onLobby(lobby);
  const onPlay = (payload: { claimerId: string; config: PlayConfig }) =>
    handlers.onPlayConfig?.(payload);
  const onWarmup = (state: BoardStatePayload) => handlers.onWarmupStart?.(state);
  const onErr = (payload: { error?: string }) =>
    handlers.onError?.(payload.error ?? 'Erreur salon');

  socketClient.on(MEETING_SOCKET_EVENTS.LOBBY_STATE, onLobby);
  socketClient.on(MEETING_SOCKET_EVENTS.PLAY_CONFIG, onPlay);
  socketClient.on(BOARD_EVENTS.WARMUP_START, onWarmup);
  socketClient.on(MEETING_SOCKET_EVENTS.ERROR, onErr);

  return () => {
    socketClient.off(MEETING_SOCKET_EVENTS.LOBBY_STATE, onLobby);
    socketClient.off(MEETING_SOCKET_EVENTS.PLAY_CONFIG, onPlay);
    socketClient.off(BOARD_EVENTS.WARMUP_START, onWarmup);
    socketClient.off(MEETING_SOCKET_EVENTS.ERROR, onErr);
    socketClient.emit(MEETING_SOCKET_EVENTS.RELEASE_DRIVER, { sessionId, claimerId });
  };
}

export function emitMeetingClaim(
  sessionId: number,
  body: { driverId: number; claimerId: string; displayName?: string; ready?: boolean }
): void {
  socketClient.emit(MEETING_SOCKET_EVENTS.CLAIM_DRIVER, { sessionId, ...body });
}

export function emitMeetingReady(sessionId: number, claimerId: string, ready: boolean): void {
  socketClient.emit(MEETING_SOCKET_EVENTS.SET_READY, { sessionId, claimerId, ready });
}

export function emitMeetingStart(sessionId: number, claimerId: string): void {
  socketClient.emit(MEETING_SOCKET_EVENTS.START, { sessionId, claimerId });
}

/** Recharge #course?meetingSession=&driverId= : rattache le socket à la manche en cours. */
export function emitMeetingReclaimRace(sessionId: number, driverId: number): void {
  if (!socketClient.connected) socketClient.connect();
  socketClient.emit(MEETING_SOCKET_EVENTS.RECLAIM_RACE, { sessionId, driverId });
}

/** Recharge #plateau?meetingSession=&driverId= : rattache le socket au tour de chauffe. */
export function emitBoardReclaim(sessionId: number, driverId: number): void {
  if (!socketClient.connected) socketClient.connect();
  socketClient.emit(BOARD_EVENTS.RECLAIM, { sessionId, driverId });
}

/**
 * Écoute la fin d'une manche meeting server-autoritaire (poussée par le serveur
 * dès que la course se termine, indépendamment de la page/lobby d'où elle a été
 * lancée) — utilisée depuis la page course (`DiceUI`), pas depuis le lobby.
 */
export function onMeetingRaceComplete(
  handler: (payload: CompleteSessionResponse) => void
): () => void {
  socketClient.on(MEETING_SOCKET_EVENTS.RACE_COMPLETE, handler);
  return () => socketClient.off(MEETING_SOCKET_EVENTS.RACE_COMPLETE, handler);
}

// --- Plateau de jeu (tour de chauffe) ---

export function emitBoardRoll(): void {
  socketClient.emit(BOARD_EVENTS.ROLL);
}

export function emitBoardDecision(accept: boolean): void {
  socketClient.emit(BOARD_EVENTS.DECISION, { accept });
}

export function emitBoardCasinoBet(betNumber: number | null, stake: number): void {
  socketClient.emit(BOARD_EVENTS.CASINO_BET, { betNumber, stake });
}

/** Prise de contrôle admin/modo d'un slot IA — voir `BOARD_EVENTS.ADMIN_TAKE_CONTROL`
 * côté serveur (`boardHandlers.ts`). */
export function emitBoardAdminTakeControl(gameId: string, playerIndex: number): void {
  socketClient.emit(BOARD_EVENTS.ADMIN_TAKE_CONTROL, { gameId, playerIndex });
}

export function onBoardState(handler: (state: BoardStatePayload) => void): () => void {
  socketClient.on(BOARD_EVENTS.STATE, handler);
  return () => socketClient.off(BOARD_EVENTS.STATE, handler);
}

export function onBoardWarmupEnd(handler: () => void): () => void {
  socketClient.on(BOARD_EVENTS.WARMUP_END, handler);
  return () => socketClient.off(BOARD_EVENTS.WARMUP_END, handler);
}

export function onBoardError(handler: (message: string) => void): () => void {
  const wrapped = (payload: { error?: string }) => handler(payload.error ?? 'Erreur plateau');
  socketClient.on(BOARD_EVENTS.ERROR, wrapped);
  return () => socketClient.off(BOARD_EVENTS.ERROR, wrapped);
}
